








import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Chart } from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import { colors } from '@/config';
import vue from 'vue';
import {
  CHART_WIDTH_BASELINE,
  highchartsSpacing,
  scaleChartPx,
} from '@/utils/chartPresentationSizing';

More(Highcharts);

@Component({ components: { Chart } })
export default class SustainabilityCompass extends Vue {
  @Prop() readonly sustainability!: any;
  @Prop() containerWidth: any;
  @Prop() currentLanguage: any;

  component: any = undefined;

  chartOptions: any = {
    chart: {
      polar: true,
      type: 'column',
      backgroundColor: 'transparent',
    },

    colors: [colors.Primary, colors.Secondary, colors.Tertiary],

    title: {
      text: '',
    },

    pane: {
      size: '90%',
      background: {
        backgroundColor: colors.Dark,
        outerRadius: '100%',
      },
    },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      itemStyle: {
        fontSize: '1cqw',
        fontWeight: 'normal',
        color: colors.LightGrey,
      },
      backgroundColor: colors.Dark,
      itemDistance: this.transformPx(50),
      margin: 0,
      padding: this.transformPx(20),
      title: {
        style: {
          fontSize: '1cqw',
          color: colors.LightGrey,
          fontWeight: 'bold',
        },
        text: 'Påverkan:',
      },
    },
    xAxis: {
      tickmarkPlacement: 'on',
      categories: [],
      labels: {
        style: {
          fontSize: '1cqw',
          color: colors.Light,
        },
        distance: this.transformPx(20),
      },
      lineColor: colors.DarkGrey,
      gridLineColor: colors.DarkGrey,
    },
    yAxis: {
      min: 2,
      max: 100,
      type: 'logarithmic',
      endOnTick: false,
      showLastLabel: true,
      labels: {
        enabled: false,
      },
      gridLineColor: colors.Primary,
      gridLineDashStyle: 'Solid',
      reversedStacks: false,
    },
    series: [],
    tooltip: {
      headerFormat: '<b>{point.key}</b><br>',
      pointFormat:
        Vue.prototype.$t('Påverkan') +
        ': <b>{series.name}</b><br> Index: {point.y:.1f}/100',
      valueSuffix: '/100',
      backgroundColor: colors.Light,
      padding: this.transformPx(20),
      style: {
        fontSize: '1.25cqw',
      },
      useHTML: true,
      distance: this.transformPx(10),
    },
    plotOptions: {
      series: {
        stacking: 'normal',
        groupPadding: 0,
        grouping: false,
        states: {
          inactive: {
            opacity: 1,
          },
        },
        point: {
          events: {
            mouseOver: this.onMouseOver,
          },
        },
        events: {
          mouseOut: this.onMouseOut,
        },
      },
    },
    exporting: {
      enabled: false,
    },
    credits: {
      text: 'Data: SIE-import ' + this.getPeriod(),
      position: {
        align: 'right',
        verticalAlign: 'bottom',
        x: 0,
        y: -10,
      },
      style: {
        fontSize: '0.75cqw',
        color: colors.LightGrey,
      },
    },
  };

  getPeriod() {
    if (!this.sustainability || !this.sustainability.PeriodStart || !this.sustainability.PeriodEnd) {
      return '';
    }

    const start = this.sustainability.PeriodStart;
    const end = this.sustainability.PeriodEnd;

    return '(' +
      start.substring(2, 4) + '-' + start.substring(5, 7) +
      ' till ' +
      end.substring(2, 4) + '-' + end.substring(5, 7) +
    ')';
  }

  onMouseOver(e: any) {
    const i = e.target.index;
    const series = this.chartOptions.series;
    const res = {
      group: this.sustainability.Groups[i].Name,
      value: this.sustainability.Groups[i].Value,
      title: e.target.category,
    };
    this.component.$emit('hover', res);
  }

  onMouseOut() {
    this.component.$emit('hover', null);
  }

  created() {
    this.component = this;
  }

  @Watch('containerWidth', { immediate: true })
  syncChartSpacing() {
    const w =
      this.containerWidth > 0 ? this.containerWidth : CHART_WIDTH_BASELINE;
    Object.assign(this.chartOptions.chart, highchartsSpacing(w));
    this.chartOptions.legend.itemDistance = scaleChartPx(50, w);
    this.chartOptions.legend.padding = scaleChartPx(20, w);
  }

  @Watch('sustainability', { immediate: true, deep: true })
  onSustainabilityChanged() {
    if (!this.sustainability) {
      return;
    }

    let categories: string[] = [];
    let s1: Array<number | null> = [];
    let s2: Array<number | null> = [];
    let s3: Array<number | null> = [];

    for (const group of this.sustainability.Groups) {
      categories = categories.concat(this.title(group.Name));
      s1 = s1.concat(this.val(group.Value, 0.015, 0.04));
      s2 = s2.concat(this.val(group.Value, 0.04, 0.2));
      s3 = s3.concat(this.val(group.Value, 0.2, 1));
    }

    this.chartOptions.xAxis.categories = categories;
    this.chartOptions.series = [
      {
        name:
          this.currentLanguage === 'en'
            ? 'Modest'
            : Vue.prototype.$t('Begränsad'),
        type: 'column',
        data: s1,
      },
      {
        name:
          this.currentLanguage === 'en'
            ? 'Minor'
            : Vue.prototype.$t('Påtaglig'),
        type: 'column',
        data: s2,
      },
      {
        name:
          this.currentLanguage === 'en'
            ? 'Major'
            : Vue.prototype.$t('Betydande'),
        type: 'column',
        data: s3,
      },
    ];
  }

  group(name: string): any | null {
    if (!this.sustainability) {
      return null;
    }
    for (const g of this.sustainability.Groups) {
      if (g.Name === name) {
        return g;
      }
    }
    return null;
  }

  val(val: number, lower: number, upper: number): number | null {
    if (val >= lower && val < upper) {
      return val * 100;
    }
    return null;
  }

  title(group: string): string {
    switch (group) {
      case '40':
        return this.currentLanguage === 'en'
          ? 'Goods & materials'
          : Vue.prototype.$t('Varor & material');
      case '46':
        return this.currentLanguage === 'en'
          ? 'Sub-contractors'
          : Vue.prototype.$t('Underentreprenörer');
      case '50':
        return this.currentLanguage === 'en'
          ? 'Rented premises'
          : Vue.prototype.$t('Lokaler');
      case '51':
        return this.currentLanguage === 'en'
          ? 'Property costs'
          : Vue.prototype.$t('Fastighetskostnader');
      case '52':
        return this.currentLanguage === 'en'
          ? 'Hired fixed assets'
          : Vue.prototype.$t('Hyra av anläggningstillgångar');
      case '53':
        return this.currentLanguage === 'en'
          ? 'Energy costs'
          : Vue.prototype.$t('Energi');
      case '54':
        return this.currentLanguage === 'en'
          ? 'Consumable equipment and supplies'
          : Vue.prototype.$t('Förbrukningsmaterial');
      case '55':
        return this.currentLanguage === 'en'
          ? 'Repairs and maintenance'
          : Vue.prototype.$t('Reparation & underhåll');
      case '56':
        return this.currentLanguage === 'en'
          ? 'Transport equipment costs'
          : Vue.prototype.$t('Transportmedel');
      case '57':
        // tslint:disable:max-line-length
        return this.currentLanguage === 'en'
          ? 'Freight and transportation'
          : Vue.prototype.$t('Frakter & transport');
      case '58':
        return this.currentLanguage === 'en'
          ? 'Travel expenses'
          : Vue.prototype.$t('Resor');
      case '59':
        return this.currentLanguage === 'en'
          ? 'Advertising and PR'
          : Vue.prototype.$t('Reklam & PR');
      case '60':
        return this.currentLanguage === 'en'
          ? 'Other selling expenses'
          : Vue.prototype.$t('Övriga försäljningskostnader');
      case '61':
        return this.currentLanguage === 'en'
          ? 'Office supplies'
          : Vue.prototype.$t('Kontorsmaterial');
      case '62':
        return this.currentLanguage === 'en'
          ? 'Telecommunications and postal services'
          : Vue.prototype.$t('Tele & post');
      case '63':
        return this.currentLanguage === 'en'
          ? 'Insurance and other risk-related costs'
          : Vue.prototype.$t('Försäkringar & riskhantering');
      case '64':
        return this.currentLanguage === 'en'
          ? 'Cost of administration'
          : Vue.prototype.$t('Förvaltning');
      case '65':
        return this.currentLanguage === 'en'
          ? 'Other external services'
          : Vue.prototype.$t('Övriga externa tjänster');
      case '68':
        return this.currentLanguage === 'en'
          ? 'Temporary employees'
          : Vue.prototype.$t('Inhyrd personal');
      case '69':
        return this.currentLanguage === 'en'
          ? 'Other external costs'
          : Vue.prototype.$t('Övriga externa kostnader');
      case '70':
        return this.currentLanguage === 'en'
          ? 'Wages and Personell costs'
          : Vue.prototype.$t('Medarbetare');
      case '74':
        return this.currentLanguage === 'en'
          ? 'Pensions'
          : Vue.prototype.$t('Pensioner');
      case '75':
        return this.currentLanguage === 'en'
          ? 'Taxes'
          : Vue.prototype.$t('Skatter');
    }
    return '';
  }

  transformPx(value: any) {
    if (value * (this.containerWidth / 1920) < 1) {
      return 1;
    }
    return value * (this.containerWidth / 1920);
  }
}
